import { LucideMenu } from "lucide-react";
import React, { ComponentProps, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { H2 } from "../displayPrimitives/Typography";

import BackButton from "./BackBtn";
import { getPersistTabAction } from "./header.utils";
import { HeaderContextProvider } from "./HeaderContext";

import { ACTIVE_TAB } from "@/app/constants/global-constants";
import { usePublishCommandPalettePageTitle } from "@/components/commandPalette/context/CommandPaletteContext";
import { useDataTableSlotContext } from "@/components/dataTable/DataTableSlotContext";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { useSheetScope } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useQueryParams from "@/hooks/query/useQueryParams";
import { toggleMobileExpanded } from "@/redux/slices/configSlice";
import { useAppDispatch } from "@/redux/store";
import { cn } from "@/utils/core/generalUtils";

export type tabs = {
  id: number;
  name: string;
  value: string;
};

export type BtnProps = ComponentProps<typeof ButtonWithIcon> & {
  btnName: string;
  showBtn: boolean;
  handleBtnClick?: () => void;
};

export type WpxTableHeaderPosition = "left" | "right" | "beforeFilter";

export type CustomPosition = {
  [K in WpxTableHeaderPosition]?: {
    node: ReactNode;
  };
};

export type HeaderProps<TabType extends string = string> = {
  hide?: {
    functions?: boolean;
    filters?: boolean;
  };
  /** Optional record label used by command-palette history instead of the visible header title. */
  commandPaletteTitle?: string;
  title?: string;
  btnProps?: BtnProps;
  link?: string;
  tabs?: tabs[];
  activeTab?: TabType;
  tabClick?: (tab: TabType) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<TabType>>;
  backBtn?: () => void;
  className?: string;
  isLoading?: boolean;
  /** When false, skips clearing table filters/sort URL state on tab change. Independent of search. */
  allowFilterReset?: boolean;
  /** When false (default), preserves table search on tab change. Set true to clear search query params. Independent of filters. */
  allowSearchReset?: boolean;
  customLeft?: ReactNode;
  customMiddle?: ReactNode;
  custom?: CustomPosition;
  meta?: {
    shouldTabPersisted?: boolean;
    persistedKeyName?: string;
  };
  disableContextFilter?: boolean;
} & React.HTMLAttributes<HTMLElement>;

const CustomHeader = <TabType extends string = string>({
  title,
  commandPaletteTitle,
  link,
  tabs,
  activeTab,
  setActiveTab,
  tabClick,
  className,
  customLeft,
  customMiddle,
  backBtn,
  isLoading,
  custom,
  btnProps,
  allowFilterReset = true,
  allowSearchReset = false,
  hide = {
    functions: false,
    filters: false,
  },
  meta,
}: HeaderProps<TabType>): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const { btnName, showBtn, handleBtnClick, ...restBtnProps } = btnProps || {};

  const navigate = useNavigate();
  const location = useLocation();
  const { updateParams, searchParams } = useQueryParams();
  const { filters, functions, resetFilter, resetSearch } =
    useDataTableSlotContext();
  const isSheetScope = useSheetScope();
  usePublishCommandPalettePageTitle({
    enabled: !isSheetScope,
    pathname: location.pathname,
    title: commandPaletteTitle ?? title,
  });

  const tabUrlKey = meta?.persistedKeyName ?? ACTIVE_TAB;
  const shouldPersistTab = meta?.shouldTabPersisted ?? !isSheetScope;
  const pendingPersistedTabRef = React.useRef<TabType | null>(null);
  const deferredPersistFrameRef = React.useRef<number | null>(null);

  const cancelDeferredPersist = React.useCallback((): void => {
    if (deferredPersistFrameRef.current === null) {
      return;
    }

    window.cancelAnimationFrame(deferredPersistFrameRef.current);
    deferredPersistFrameRef.current = null;
  }, []);

  const persistTabAfterPaint = React.useCallback(
    (tab: TabType): void => {
      if (!shouldPersistTab) {
        return;
      }

      pendingPersistedTabRef.current = tab;
      cancelDeferredPersist();

      deferredPersistFrameRef.current = window.requestAnimationFrame(() => {
        deferredPersistFrameRef.current = window.requestAnimationFrame(() => {
          deferredPersistFrameRef.current = null;
          updateParams(tabUrlKey, tab);
        });
      });
    },
    [cancelDeferredPersist, shouldPersistTab, tabUrlKey, updateParams],
  );

  React.useLayoutEffect(() => {
    if (!tabs?.length || !activeTab || !setActiveTab || !shouldPersistTab) {
      return;
    }

    const persistedTab = searchParams.get(tabUrlKey);
    const persistTabAction = getPersistTabAction({
      tabs,
      activeTab,
      pendingTab: pendingPersistedTabRef.current,
      persistedTab,
    });

    if (
      pendingPersistedTabRef.current === activeTab &&
      persistedTab === activeTab
    ) {
      pendingPersistedTabRef.current = null;
    }

    if (persistTabAction.type === "hydrate") {
      setActiveTab(persistTabAction.tab);
      return;
    }

    if (persistTabAction.type === "none") {
      return;
    }

    updateParams(tabUrlKey, persistTabAction.tab);
  }, [
    tabs,
    activeTab,
    setActiveTab,
    shouldPersistTab,
    tabUrlKey,
    searchParams,
    updateParams,
  ]);

  React.useEffect(() => {
    return cancelDeferredPersist;
  }, [cancelDeferredPersist]);

  const handleClick = (): void => {
    if (handleBtnClick) {
      handleBtnClick();
    } else if (link) {
      navigate(link);
    }
  };

  const handleTabClick = (tab: TabType): void => {
    if (activeTab === tab) {
      return;
    }

    if (setActiveTab) {
      setActiveTab(tab);
      // Per-tab pagination is keyed by activeTab in IndexedDB, so each tab
      // restores its own last page on switch. Do not call resetPagination here.
      if (allowSearchReset) {
        resetSearch?.();
      }
      if (allowFilterReset) {
        resetFilter?.();
      }
      if (shouldPersistTab) {
        persistTabAfterPaint(tab);
      }
    }
    tabClick?.(tab);
  };

  const headerContextValue = React.useMemo(
    () => ({ hasTabs: !!tabs?.length }),
    [tabs?.length],
  );

  return (
    <HeaderContextProvider value={headerContextValue}>
      <section className={cn("mt-4 min-w-0", className)}>
        <div className='flex w-full min-w-0 flex-col gap-2 mb-2 md:flex-row md:items-center md:gap-3'>
          {title && (
            <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
              <div
                className='lg:hidden pr-1'
                onClick={() => dispatch(toggleMobileExpanded())}
              >
                <LucideMenu size={24} />
              </div>
              {backBtn && <BackButton backBtn={backBtn} />}
              <H2 className='min-w-0 break-all line-clamp-1' title={title}>
                {title}
              </H2>
              <div className='flex items-center ml-12'>
                {customLeft && customLeft}
              </div>
            </div>
          )}

          {customMiddle && (
            <div className='absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex items-center pointer-events-none'>
              <div className='pointer-events-auto'>{customMiddle}</div>
            </div>
          )}

          <div className='flex w-full shrink-0 items-center gap-2 md:ml-auto md:w-auto'>
            {custom?.left && custom.left.node && custom.left.node}
            {!hide?.functions && functions ? functions : null}
            {custom?.right && custom.right.node && custom.right.node}
            {showBtn && (
              <ButtonWithIcon
                isLoading={isLoading}
                icon={btnProps?.icon}
                variant={btnProps?.variant ?? "default"}
                className={cn(btnProps?.className)}
                onClick={handleClick}
                {...restBtnProps}
              >
                <p>{btnName}</p>
              </ButtonWithIcon>
            )}
          </div>
        </div>
        <div className='flex flex-col md:flex-row md:items-center justify-between'>
          {tabs && activeTab && setActiveTab && (
            <Tabs
              value={activeTab as string}
              onValueChange={(value) => handleTabClick(value as TabType)}
              activationMode='manual'
            >
              <TabsList className={cn("flex w-fit border p-2 my-2")}>
                {tabs.map((tab) => {
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.value}
                      className={cn("text-xs")}
                    >
                      {tab?.name}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          )}

          <div className='flex flex-1 items-center space-x-3 min-w-0'>
            {!hide?.filters && filters && filters}
          </div>
        </div>
      </section>
    </HeaderContextProvider>
  );
};

export default CustomHeader;
